import { usePolarityDomain } from "@/shared/types/polarity";
import { formatCompact, formatInteger } from "@/shared/utils/format";

import { ChartLegend, polarityLegend, polaritySegments, useChartTip, useChartWidth } from "./chart-kit";
import { EmptyState } from "./EmptyState";

export type PolarityColumnPoint = {
  key: string;
  label: string;
  total: number;
  byPolarity: Record<string, number>;
};

const HEIGHT = 240;
const PAD = { top: 12, right: 8, bottom: 28, left: 44 };
const GAP = 2;

/**
 * A série no tempo empilhada por sentimento — a metade de volume da
 * "Evolução por Sentimento & CENA" do v1. A metade de CENA é outro
 * gráfico, logo abaixo, e não uma linha sobre estas colunas: cada um tem
 * o seu eixo (ver `PolarityBars`).
 */
export function PolarityColumns({ points, percent = false }: { points: PolarityColumnPoint[]; percent?: boolean }) {
  usePolarityDomain();
  const [frame, width] = useChartWidth<HTMLDivElement>();
  const { bind, tip } = useChartTip(frame);
  if (!points.length || points.every(point => point.total === 0)) return <EmptyState/>;

  const plotWidth = Math.max(80, width - PAD.left - PAD.right);
  const plotHeight = HEIGHT - PAD.top - PAD.bottom;
  const max = percent ? 100 : Math.max(...points.map(point => point.total), 1);
  const slot = plotWidth / points.length;
  const columnWidth = Math.max(3, Math.min(36, slot * 0.66));
  const every = Math.max(1, Math.ceil(points.length / Math.max(1, Math.floor(plotWidth / 64))));
  const ticks = [0, 0.5, 1];
  const legend = polarityLegend(points.map(point => point.byPolarity), percent);

  return (
    <div className="chart-frame" ref={frame}>
      <svg data-export width={width} height={HEIGHT} viewBox={`0 0 ${width} ${HEIGHT}`} role="img" aria-label={`Volume por sentimento em ${points.length} períodos`}>
        {ticks.map(step => {
          const y = PAD.top + plotHeight * (1 - step);
          return (
            <g key={step}>
              <line x1={PAD.left} x2={width - PAD.right} y1={y} y2={y} className="chart-grid-line"/>
              <text x={PAD.left - 6} y={y} className="chart-axis-text" textAnchor="end" dominantBaseline="middle">
                {percent ? `${Math.round(step * 100)}%` : formatCompact(max * step)}
              </text>
            </g>
          );
        })}
        {points.map((point, index) => {
          const x = PAD.left + slot * index + (slot - columnWidth) / 2;
          let cursor = PAD.top + plotHeight;
          const segments = polaritySegments(point.byPolarity, { percent });
          const detail = polaritySegments(point.byPolarity).filter(segment => segment.value > 0);
          return (
            <g
              key={point.key}
              tabIndex={0}
              className="chart-column"
              aria-label={`${point.label}: ${formatInteger(point.total)} menções`}
              {...bind({
                title: point.label,
                lines: [
                  { label: "Total", value: formatInteger(point.total) },
                  ...detail.map(segment => ({
                    label: segment.label,
                    tone: segment.tone,
                    value: `${formatInteger(segment.value)}${point.total ? ` · ${Math.round((segment.value / point.total) * 100)}%` : ""}`
                  }))
                ]
              })}
            >
              <rect x={PAD.left + slot * index} y={PAD.top} width={slot} height={plotHeight} className="chart-row-hit"/>
              {segments.map(segment => {
                const length = (segment.value / max) * plotHeight;
                if (length <= 0) return null;
                cursor -= length;
                return (
                  <rect
                    key={segment.key}
                    x={x}
                    y={cursor}
                    width={columnWidth}
                    height={Math.max(length - GAP, 1)}
                    rx={Math.min(3, columnWidth / 3)}
                    className={`chart-tone--${segment.tone}`}
                  />
                );
              })}
              {index % every === 0 && (
                <text x={PAD.left + slot * index + slot / 2} y={HEIGHT - 8} className="chart-axis-text" textAnchor="middle">{point.label}</text>
              )}
            </g>
          );
        })}
      </svg>
      {tip}
      <ChartLegend items={legend}/>
    </div>
  );
}
